import {ArchiveIcon} from '@/components/svg/ArchiveIcon';
import {ChatIcon} from '@/components/svg/ChatIcon';
import {useApi, useMe} from '@/haligator-factory';
import {usePeutVoirToutesLesConversations} from '@/hooks/auth/usePermissionsCommerciales';
import {
  Box,
  Button,
  HStack,
  IconButton,
  Skeleton,
  SkeletonCircle,
  Text,
  VStack,
} from '@chakra-ui/react';
import {ChevronDownIcon, ChevronRightIcon} from '@chakra-ui/icons';
import {useHalCollection, useHalState} from '@synako/haligator';
import {Navigable} from '@synako/halitrek';
import {ConversationDesc, ParticipationDesc} from 'api-types/ffconnect-hal';
import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {ConversationClientLine} from './ConversationClientLine';
import {ConversationClientLineConversation} from './ConversationClientLineConversation';
import {BadgeConversationsNonLues} from './BadgeConversationsNonLues';
import {ConversationsContactsTiroir} from './ConversationsContactsTiroir';

/** Skeleton pour une ligne de conversation côté client */
const ConversationClientLineSkeleton: React.FC = () => (
  <HStack
    spacing={3}
    pb={3}
    w='100%'
  >
    <SkeletonCircle size='8' />
    <VStack
      align='flex-start'
      spacing={1}
      flex={1}
    >
      <Skeleton
        height='14px'
        width='70%'
      />
      <Skeleton
        height='12px'
        width='50%'
      />
    </VStack>
  </HStack>
);

// Onglets disponibles : mes conversations (en cours), toutes (périmètre, commercial), terminées
export type OngletConversation = 'en_cours' | 'toutes' | 'terminee';

/**
 * Contenu de l'onglet "Toutes les conversations" dans la popin (fiche client / mode commercial).
 * Charge les conversations de ce client (avec n'importe quel commercial) via
 * `find-conversations?perimetre=true&client_id=...` : le filtre périmètre borne l'accès
 * (réservé aux utilisateurs FF/Synako autorisés) et `client_id` restreint à la fiche courante,
 * avec le bon `isParticipant` (lien "Rejoindre" sur les conversations d'un collègue).
 * La présence de l'onglet reste conditionnée par le lien HAL `conversations-perimetre` (capability).
 * `statut` permet de réutiliser cette vue périmètre pour l'onglet "Conversations terminées".
 * Isolé dans un sous-composant pour que le fetch n'ait lieu que lorsque l'onglet est rendu.
 */
const OngletToutesContent: React.FC<{
  clientId?: string;
  statut?: 'en_cours' | 'terminee';
  onOuvrirConversation: (conversationNav: Navigable<ConversationDesc>) => void;
}> = ({clientId, statut = 'en_cours', onOuvrirConversation}) => {
  const {t} = useTranslation();
  const api = useApi();
  const {items, loading, error} = useHalCollection(
    api.follow(
      'find-conversations',
      clientId ? {perimetre: true, client_id: clientId} : {perimetre: true},
    ),
  );

  // Le périmètre liste toutes les conversations ; on ne garde ici que celles du statut demandé.
  const conversations = useMemo(
    () => (items ?? []).filter((c) => c.data.statut === statut),
    [items, statut],
  );

  if (loading) {
    return (
      <VStack
        spacing={2}
        align='stretch'
        w='100%'
      >
        <ConversationClientLineSkeleton />
        <ConversationClientLineSkeleton />
        <ConversationClientLineSkeleton />
      </VStack>
    );
  }

  if (error) {
    return (
      <Text
        color='red.500'
        textAlign='center'
        py={4}
      >
        {t('global.erreur_chargement')}
      </Text>
    );
  }

  if (conversations.length === 0) {
    return (
      <Text
        color='gray.dark'
        textAlign='center'
        py={4}
      >
        {statut === 'terminee'
          ? t('client.conversations.aucune_discussion_cloturee')
          : t('client.conversations.aucune_conversation')}
      </Text>
    );
  }

  return (
    <VStack
      spacing={0}
      align='stretch'
      w='100%'
      divider={
        conversations.length > 1 ? (
          <Box
            borderBottom='1px solid'
            borderColor='gray.light'
          />
        ) : undefined
      }
    >
      {conversations.map((conversationState) => (
        <ConversationClientLineConversation
          key={conversationState.data.id}
          conversationState={conversationState}
          onClick={() => {
            onOuvrirConversation(conversationState);
          }}
        />
      ))}
    </VStack>
  );
};

interface ConversationsClientListeViewProps {
  // Client de la fiche / du contexte commercial : restreint l'onglet "Toutes les conversations"
  // aux conversations de ce client (cf. OngletToutesContent).
  clientId?: string;
  onFermer: () => void;
  onNouvelleConversation: () => void; // Ouvrir la vue pour initier une nouvelle conversation
  onOuvrirConversation: (
    participationNav: Navigable<ParticipationDesc>,
  ) => void; // Ouvrir le détail d'une conversation existante (participation)
  onOuvrirConversationPerimetre: (
    conversationNav: Navigable<ConversationDesc>,
  ) => void; // Ouvrir une conversation du périmètre (onglet "Toutes")
  // Onglet actif contrôlé par la modale parente, pour qu'il survive aux allers-retours
  // vers le détail d'une conversation (la liste est démontée pendant l'affichage du détail).
  ongletActif: OngletConversation;
  onChangeOnglet: (onglet: OngletConversation) => void;
}

/**
 * Vue liste des conversations côté client.
 * Affiche les conversations de l'utilisateur (en cours / terminées). En mode commercial,
 * un onglet "Toutes les conversations" (vue périmètre) s'ajoute si le lien HAL est présent.
 */
export const ConversationsClientListeView: React.FC<
  ConversationsClientListeViewProps
> = ({
  clientId,
  onFermer,
  onNouvelleConversation,
  onOuvrirConversation,
  onOuvrirConversationPerimetre,
  ongletActif,
  onChangeOnglet,
}) => {
  const {t} = useTranslation();
  const me = useMe();
  const api = useApi();

  // Sous-titre du header : en fiche client / mode commercial, un clientId explicite est fourni
  // (jamais en interface client) → on affiche le client concerné ("avec {raison_sociale}") au lieu
  // du message générique destiné au contact client.
  const clientState = useHalState(
    clientId ? api.follow('get-client', {client_id: clientId}) : undefined,
  );
  const raisonSociale = clientState.data?.tolede.raison_sociale;

  // Onglet "Toutes les conversations" disponible uniquement si le lien HAL est présent
  // (utilisateur FF/Synako autorisé, gestion par lien HAL côté backend).
  const peutVoirToutes = usePeutVoirToutesLesConversations();

  // Récupération des participations de l'utilisateur aux conversations.
  // En fiche client / mode commercial (clientId fourni), on restreint "Mes conversations" et
  // "Conversations terminées" aux conversations de ce client (lien me templated sur client_id).
  // Sans clientId (interface client), on récupère toutes mes participations (le contact_client
  // ne voit que son propre client).
  const {items, loading, error} = useHalCollection(
    me.follow(
      'participations-conversation',
      clientId ? {client_id: clientId} : {},
    ),
  );

  // Séparer les conversations en cours et terminées (mes participations)
  const participationItems = useMemo(() => items ?? [], [items]);
  const conversationsEnCours = useMemo(
    () =>
      participationItems.filter(
        (p) => p.data.conversation_statut === 'en_cours',
      ),
    [participationItems],
  );
  const conversationsTerminees = useMemo(
    () =>
      participationItems.filter(
        (p) => p.data.conversation_statut === 'terminee',
      ),
    [participationItems],
  );

  // Conversations (participations) à afficher pour les onglets "Mes" / "Terminées" (mode participation)
  const conversationsAAfficher =
    ongletActif === 'terminee' ? conversationsTerminees : conversationsEnCours;

  // Vue périmètre (toutes les conversations du client, comme le BO) plutôt que mes participations :
  // onglet "Toutes", et onglet "Terminées" quand le commercial a l'accès périmètre. Sinon "Terminées"
  // reste sur mes participations terminées.
  const afficherPerimetre =
    ongletActif === 'toutes' || (ongletActif === 'terminee' && peutVoirToutes);

  return (
    <Box
      display='flex'
      flexDirection='column'
      h='100%'
    >
      {/* Header */}
      <HStack
        as='header'
        justify='space-between'
        align='center'
        borderBottom='1px solid'
        borderColor='gray.light'
        py={3}
        px={4}
      >
        <VStack
          align='flex-start'
          spacing={0}
        >
          <Text
            fontSize='md'
            fontWeight='700'
            color='black.text'
          >
            {t('client.conversations.titre')}
          </Text>
          <Text
            fontSize='xs'
            fontWeight='400'
            color='gray.dark'
          >
            {raisonSociale
              ? t('client.conversations.sous_titre_commercial', {
                  raison_sociale: raisonSociale,
                })
              : t('client.conversations.sous_titre')}
          </Text>
        </VStack>
        <IconButton
          aria-label={t('client.conversations.reduire_chatbox')}
          icon={<ChevronDownIcon boxSize={6} />}
          variant='ghost'
          color='gray.dark'
          _hover={{bg: 'gray.bg'}}
          onClick={onFermer}
        />
      </HStack>

      {/* Zone de contenu principal */}
      <Box
        flex={1}
        w='100%'
        p={3}
        pt={0}
        overflowY='auto'
        position='relative'
      >
        {/* Tiroir contacts : interface client uniquement (pas Vue Commerce / fiche BO) */}
        {!clientId && <ConversationsContactsTiroir />}

        {/* Vue périmètre : onglet "Toutes" (en cours) ou "Terminées" si accès périmètre */}
        {afficherPerimetre ? (
          <Box mt={3}>
            <OngletToutesContent
              clientId={clientId}
              statut={ongletActif === 'terminee' ? 'terminee' : 'en_cours'}
              onOuvrirConversation={onOuvrirConversationPerimetre}
            />
          </Box>
        ) : (
          <>
            {/* État de chargement */}
            {loading && (
              <VStack
                spacing={2}
                align='stretch'
                w='100%'
              >
                <ConversationClientLineSkeleton />
                <ConversationClientLineSkeleton />
                <ConversationClientLineSkeleton />
              </VStack>
            )}

            {/* État d'erreur */}
            {error && !loading && (
              <Text
                color='red.500'
                textAlign='center'
                py={4}
              >
                {t('global.erreur_chargement')}
              </Text>
            )}

            {/* Contenu chargé */}
            {!loading && !error && conversationsAAfficher.length === 0 ? (
              // Aucune conversation - invitation à commencer une conversation
              <VStack
                spacing={4}
                py={4}
                mt={3}
                bg='blue.sky_light'
                borderRadius='8px'
              >
                {/* Message d'accroche réservé à l'interface client : masqué en
                    mode commercial (clientId fourni en fiche client) */}
                {!clientId && (
                  <Text
                    fontSize='md'
                    fontWeight='600'
                    color='blue.main'
                    textAlign='center'
                  >
                    {t('client.conversations.equipe_aide')}
                  </Text>
                )}

                <Button
                  bg='blue.main'
                  color='white'
                  size='sm'
                  borderRadius='8px'
                  rightIcon={<ChevronRightIcon boxSize={5} />}
                  _hover={{bg: 'blue.dark'}}
                  onClick={onNouvelleConversation}
                >
                  {t('client.conversations.commencer_conversation')}
                </Button>
              </VStack>
            ) : !loading && !error && conversationsAAfficher.length > 0 ? (
              // Conversations existantes - Liste des conversations avec scroll
              <VStack
                spacing={0}
                align='stretch'
                w='100%'
                divider={
                  conversationsAAfficher.length > 1 ? (
                    <Box
                      borderBottom='1px solid'
                      borderColor='gray.light'
                    />
                  ) : undefined
                }
              >
                {conversationsAAfficher.map((participationState) => (
                  <ConversationClientLine
                    key={participationState.data.id}
                    participationState={participationState}
                    onClick={() => {
                      onOuvrirConversation(participationState);
                    }}
                  />
                ))}
              </VStack>
            ) : null}
          </>
        )}
      </Box>

      {/* Bouton nouvelle conversation flottant au-dessus de la liste (onglets "Mes"/"Terminées" hors vue périmètre) */}
      {!afficherPerimetre &&
        !loading &&
        !error &&
        conversationsAAfficher.length > 0 && (
          <Box
            position='absolute'
            bottom={{base: '155px', md: '75px'}}
            left={0}
            right={0}
            display='flex'
            justifyContent='center'
          >
            <Button
              bg='blue.main'
              color='white'
              size='md'
              fontSize='xs'
              borderRadius='8px'
              rightIcon={<ChevronRightIcon boxSize={5} />}
              _hover={{bg: 'blue.dark'}}
              onClick={onNouvelleConversation}
            >
              {t('client.conversations.commencer_conversation')}
            </Button>
          </Box>
        )}

      {/* Onglets en bas (Mes conversations / Toutes / Terminées) */}
      <HStack
        borderTop='1px solid'
        borderColor='gray.light'
        pt={2}
        pb={{base: '5rem', md: 2}}
        spacing={0}
      >
        {/* Onglet "Mes conversations" (en cours) */}
        <Box
          flex={1}
          display='flex'
          flexDirection='column'
          alignItems='center'
          cursor='pointer'
          color={ongletActif === 'en_cours' ? 'blue.main' : 'gray.dark'}
          onClick={() => {
            onChangeOnglet('en_cours');
          }}
        >
          <Box position='relative'>
            <ChatIcon
              width='20px'
              height='20px'
            />
            <BadgeConversationsNonLues
              clientId={clientId}
              top='-8px'
              right='-12px'
            />
          </Box>
          <Text
            fontSize='xs'
            mt={1}
          >
            {t('client.conversations.onglet_en_cours')}
          </Text>
        </Box>

        {/* Onglet "Toutes les conversations" (périmètre, mode commercial uniquement) */}
        {peutVoirToutes && (
          <Box
            flex={1}
            display='flex'
            flexDirection='column'
            alignItems='center'
            cursor='pointer'
            color={ongletActif === 'toutes' ? 'blue.main' : 'gray.dark'}
            onClick={() => {
              onChangeOnglet('toutes');
            }}
          >
            <ChatIcon
              width='20px'
              height='20px'
            />
            <Text
              fontSize='xs'
              mt={1}
            >
              {t('client.conversations.onglet_toutes')}
            </Text>
          </Box>
        )}

        {/* Onglet "Conversations terminées" */}
        <Box
          flex={1}
          display='flex'
          flexDirection='column'
          alignItems='center'
          cursor='pointer'
          color={ongletActif === 'terminee' ? 'blue.main' : 'gray.dark'}
          onClick={() => {
            onChangeOnglet('terminee');
          }}
        >
          <ArchiveIcon
            width='20px'
            height='20px'
          />
          <Text
            fontSize='xs'
            mt={1}
          >
            {t('client.conversations.onglet_terminees')}
          </Text>
        </Box>
      </HStack>
    </Box>
  );
};
